import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { fmtCurrency } from "../lib/format";
import { TopBar } from "../components/TopBar";
import { TotalsCard } from "../components/TotalsCard";
import { PeriodBadge } from "../components/PeriodBadge";
import { WeekComparison } from "../components/WeekComparison";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Plus, Loader2, Share2, CheckCircle2, Search, ArrowUpDown } from "lucide-react";
import { toast } from "sonner";
import { cn } from "../lib/utils";

const SORT_OPTIONS = [
  { value: "name-asc", label: "Name (A → Z)" },
  { value: "name-desc", label: "Name (Z → A)" },
  { value: "ph-desc", label: "PH balance (high → low)" },
  { value: "ph-asc", label: "PH balance (low → high)" },
  { value: "qg-desc", label: "QG balance (high → low)" },
  { value: "qg-asc", label: "QG balance (low → high)" },
];

export default function SourcesPortalPage() {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("name-asc");

  const visibleSources = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = q
      ? sources.filter((s) => (s.name || "").toLowerCase().includes(q))
      : [...sources];
    const byName = (a, b) =>
      (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" });
    filtered.sort((a, b) => {
      switch (sortBy) {
        case "name-desc":
          return byName(b, a);
        case "ph-desc":
          return (b.ph_total ?? 0) - (a.ph_total ?? 0) || byName(a, b);
        case "ph-asc":
          return (a.ph_total ?? 0) - (b.ph_total ?? 0) || byName(a, b);
        case "qg-desc":
          return (b.qg_total ?? 0) - (a.qg_total ?? 0) || byName(a, b);
        case "qg-asc":
          return (a.qg_total ?? 0) - (b.qg_total ?? 0) || byName(a, b);
        case "name-asc":
        default:
          return byName(a, b);
      }
    });
    return filtered;
  }, [sources, search, sortBy]);

  const load = useCallback(async () => {
    try {
      const [dashRes, listRes] = await Promise.all([
        api.get("/sources/dashboard"),
        api.get("/sources"),
      ]);
      setDashboard(dashRes.data);
      setSources(listRes.data);
    } catch (e) {
      toast.error("Failed to load sources data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const createSource = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const res = await api.post("/sources", { name: newName.trim() });
      setDialogOpen(false);
      setNewName("");
      navigate(`/sources/${res.data.id}`);
    } catch (e) {
      toast.error("Failed to create source");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="min-h-screen bg-muted/30">
      <TopBar crumbs={[{ label: "Sources" }]} />
      <main className="mx-auto max-w-7xl space-y-6 px-4 py-8 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">Sources Dashboard</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              PH and QG balances aggregated from every source.
            </p>
          </div>
          <PeriodBadge />
        </div>

        {loading ? (
          <div className="flex h-40 items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              <TotalsCard
                label="Total Available Balance PH"
                value={dashboard?.total_available_balance_ph ?? 0}
                helper="Sum of PH totals across all sources"
                testId="sources-total-available-balance-ph"
                accent
              />
              <TotalsCard
                label="Total Available Balance QG"
                value={dashboard?.total_available_balance_qg ?? 0}
                helper="Sum of QG totals across all sources"
                testId="sources-total-available-balance-qg"
                accent
              />
            </div>

            <WeekComparison
              testId="sources-week-comparison"
              lastWeek={dashboard?.last_week}
              rows={
                dashboard?.last_week
                  ? [
                      {
                        label: "PH Expenses",
                        current: dashboard.current_ph_expenses ?? 0,
                        last: dashboard.last_week.ph_expenses ?? 0,
                        invert: true,
                      },
                      {
                        label: "PH Total",
                        current: dashboard.total_available_balance_ph ?? 0,
                        last: dashboard.last_week.ph_total ?? 0,
                      },
                      {
                        label: "QG Expenses",
                        current: dashboard.current_qg_expenses ?? 0,
                        last: dashboard.last_week.qg_expenses ?? 0,
                        invert: true,
                      },
                      {
                        label: "QG Total",
                        current: dashboard.total_available_balance_qg ?? 0,
                        last: dashboard.last_week.qg_total ?? 0,
                      },
                    ]
                  : []
              }
            />

            <Card className="shadow-sm">
              <CardHeader className="flex flex-col gap-3 space-y-0 pb-3 sm:flex-row sm:items-center sm:justify-between">
                <CardTitle className="text-base font-semibold">List of Sources</CardTitle>
                {sources.length > 0 && (
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                    <div className="relative">
                      <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        data-testid="sources-search-input"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search source name…"
                        className="h-9 w-full pl-8 sm:w-56"
                      />
                    </div>
                    <Select value={sortBy} onValueChange={setSortBy}>
                      <SelectTrigger className="h-9 w-full sm:w-56" data-testid="sources-sort-select">
                        <ArrowUpDown className="mr-1.5 h-3.5 w-3.5 text-muted-foreground" />
                        <SelectValue placeholder="Sort by" />
                      </SelectTrigger>
                      <SelectContent>
                        {SORT_OPTIONS.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value} data-testid={`sources-sort-${opt.value}`}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </CardHeader>
              <CardContent>
                {sources.length === 0 ? (
                  <div className="flex flex-col items-center justify-center rounded-md border border-dashed border-border py-12 text-center">
                    <Share2 className="h-8 w-8 text-muted-foreground" />
                    <p className="mt-3 text-sm font-medium">No sources yet</p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Add your first source to start tracking PH / QG balances.
                    </p>
                  </div>
                ) : visibleSources.length === 0 ? (
                  <div className="flex flex-col items-center justify-center rounded-md border border-dashed border-border py-12 text-center" data-testid="sources-no-match">
                    <Search className="h-8 w-8 text-muted-foreground" />
                    <p className="mt-3 text-sm font-medium">No sources match “{search}”</p>
                    <p className="mt-1 text-xs text-muted-foreground">Try a different search term.</p>
                  </div>
                ) : (
                  <div className="w-full overflow-x-auto rounded-md border border-border">
                    <Table data-testid="sources-list-table">
                      <TableHeader>
                        <TableRow>
                          <TableHead>Source</TableHead>
                          <TableHead className="text-right">PH Balance</TableHead>
                          <TableHead className="text-right">QG Balance</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {visibleSources.map((s, idx) => (
                          <TableRow
                            key={s.id}
                            data-testid={`source-list-row-${idx}`}
                            onClick={() => navigate(`/sources/${s.id}`)}
                            className="cursor-pointer transition-colors duration-150 hover:bg-muted/60"
                          >
                            <TableCell className="font-medium">
                              <span className="flex items-center gap-2">
                                {s.name}
                                {s.week_confirmed && (
                                  <span
                                    data-testid={`source-list-row-${idx}-confirmed-badge`}
                                    className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700"
                                  >
                                    <CheckCircle2 className="h-3 w-3" />
                                    Confirmed
                                  </span>
                                )}
                              </span>
                            </TableCell>
                            <TableCell
                              className={cn(
                                "text-right font-mono tabular-nums",
                                s.ph_total < 0 ? "text-red-600" : "text-foreground"
                              )}
                            >
                              {fmtCurrency(s.ph_total)}
                            </TableCell>
                            <TableCell
                              className={cn(
                                "text-right font-mono tabular-nums",
                                s.qg_total < 0 ? "text-red-600" : "text-foreground"
                              )}
                            >
                              {fmtCurrency(s.qg_total)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}

                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="mt-4" data-testid="sources-add-source-button">
                      <Plus className="mr-1.5 h-4 w-4" />
                      Add Source
                    </Button>
                  </DialogTrigger>
                  <DialogContent data-testid="add-source-dialog">
                    <DialogHeader>
                      <DialogTitle>Add Source</DialogTitle>
                      <DialogDescription>Create a new source to track its PH / QG balances.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-1.5 py-2">
                      <Label htmlFor="source-name">Source name</Label>
                      <Input
                        id="source-name"
                        data-testid="add-source-name-input"
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        placeholder="e.g. Source A"
                        onKeyDown={(e) => e.key === "Enter" && createSource()}
                        autoFocus
                      />
                    </div>
                    <DialogFooter>
                      <Button
                        onClick={createSource}
                        disabled={creating || !newName.trim()}
                        data-testid="add-source-submit-button"
                      >
                        {creating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Add Source
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </CardContent>
            </Card>
          </>
        )}
      </main>
    </div>
  );
}
